import * as React from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

// ===============================================================================

//
// Generic React error boundary. Catches rendering errors thrown by its children
// so a single bad component (e.g. one relying on stale/missing data) shows a
// contained fallback message instead of unmounting the entire app.
//
// See https://reactjs.org/docs/error-boundaries.html
//

interface ErrorBoundaryProps {
    fallback?: React.ReactNode;
    // when no explicit fallback is given, used to localize the default fallback message
    localizer?: IStringLocalizer;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);

        this.state = { hasError: false };
    }

    public static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // eslint-disable-next-line no-console
        console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            const defaultMessage = this.props.localizer
                ? this.props.localizer.localizeString("Something went wrong loading this section. Please refresh the page and try again.")
                : "Something went wrong loading this section. Please refresh the page and try again.";

            return this.props.fallback ?? (
                <div className="alert alert-danger">
                    {defaultMessage}
                </div>
            );
        }

        return this.props.children;
    }
}
